import Cookies from "js-cookie";
import { TrackableEventNameEnum } from "react-use-facebook-pixel";
import { useAppContext } from "../components/organisms/Layout/context";
import {
  WEBSITE_VARIANT,
  CANCELLATION_CLOSE_TRAY,
  WINE_LIST_TYPE_IDS,
  FACEBOOK_TRACKING_CURRENCY,
  ERROR_CODE_ITERABLE_GDPR_FORGOTTEN,
  RESPONSE_CODE_ITERABLE_SUCCESS,
  ERROR_MESSAGE_ITERABLE_USER_UNAUTHORIZED,
  ERROR_MESSAGE_ITERABLE_CONFLICT,
  ERROR_MESSAGE_ITERABLE_BAD_REQUEST,
  ERROR_CODE_ITERABLE_FORGOTTEN_USER,
  ERROR_MESSAGE_ITERABLE_NETWORK,
} from "./constants";
import { isLoggedIn } from "./auth";
import { logError } from "./logging";

// Iterable's web-sdk is ~84 KB gzipped and was being pulled into the eager
// bundle via this module. Layout fires its own dynamic `import()` before any
// of the call sites here are reached (each is gated on `isIterableReady`),
// so by the time we await this loader the module is already cached.
const loadIterable = () => import("@iterable/web-sdk");

/* The Iterable web JS SDK only fires if the website API key is set. */
const hasIterableApiKey = () => !!import.meta.env.REACT_APP_ITERABLE_API_KEY;

const trackingEvents = {
  TRACKING_EVENT_VISITED_PAGE: "visited_page",
  TRACKING_EVENT_STARTED_QUIZ: "started_quiz",
  TRACKING_EVENT_VIEWED_QUIZ_STEP: "viewed_quiz_step",
  TRACKING_EVENT_ANSWERED_QUIZ_QUESTION: "answered_quiz_question",
  TRACKING_EVENT_SWITCHED_QUIZ_VERSION: "switched_quiz_version",
  TRACKING_EVENT_RECOGNIZED_EXISTING_QUIZ_USER: "recognized_existing_quiz_user",
  TRACKING_EVENT_COMPLETED_QUIZ: "completed_quiz",
  TRACKING_EVENT_RETOOK_QUIZ: "retook_quiz",
  TRACKING_EVENT_STARTED_PALATE_EDIT: "started_palate_edit",
  TRACKING_EVENT_SELECTED_PALATE_TRAIT_OPTION: "selected_palate_trait_option",
  TRACKING_EVENT_SUBMITTED_PALATE_EDIT: "submitted_palate_edit",
  TRACKING_EVENT_COMPLETED_PALATE_EDIT: "completed_palate_edit",
  TRACKING_EVENT_STARTED_CHECKOUT: "started_checkout",
  TRACKING_EVENT_PLACED_ORDER: "placed_order",
  TRACKING_EVENT_SELECTED_GIFT_OPTION: "selected_gift_option",
  TRACKING_EVENT_COPIED_REFERRAL_LINK: "copied_referral_link",
  TRACKING_EVENT_CLICKED_WINE_SHARE_BUTTON: "clicked_wine_share_button",
  TRACKING_EVENT_COPIED_WINE_SHARE_LINK: "copied_wine_share_link",
  TRACKING_EVENT_STARTED_CANCELLATION: "started_cancellation_wizard",
  TRACKING_EVENT_DROPPED_CANCELLATION: "dropped_cancellation_wizard",
  TRACKING_EVENT_OPENED_SAVED_LISTS: "opened_saved_lists",
  TRACKING_EVENT_VIEWED_SAVED_LIST_DETAILS: "viewed_saved_list_details",
  TRACKING_EVENT_CLICKED_POPUP_CTA_BUTTON: "clicked_popup_cta_button",
  TRACKING_EVENT_UPDATED_BOX_AND_SPEND: "updated_box_and_spend",
  TRACKING_EVENT_UPDATED_BILLING_DATE: "updated_billing_date",
  TRACKING_EVENT_UPDATED_DELIVERY_FREQUENCY: "updated_delivery_frequency",
  TRACKING_EVENT_UPDATED_PERSONAL_INFORMATION: "updated_personal_information",
  TRACKING_EVENT_UPDATED_GRAPES_TO_AVOID: "updated_grapes_to_avoid",
  TRACKING_EVENT_CONTACTED_SUPPORT_TEAM: "contacted_support_team",
  TRACKING_EVENT_SEARCHED_WINES: "searched_wines",
  TRACKING_EVENT_SORTED_WINES: "sorted_wines",
  TRACKING_EVENT_FILTERED_WINES: "filtered_wines",
  TRACKING_EVENT_CLICKED_TAG_ON_WINE_CARD: "clicked_tag_on_wine_card",
  TRACKING_EVENT_CLICKED_TAG_ON_PRODUCT_CARD: "clicked_tag_on_product_card",
  TRACKING_EVENT_SELECTED_PRODUCT_RATING: "selected_product_rating",
  TRACKING_EVENT_SELECTED_PRODUCT_RATING_DETAIL: "selected_product_rating_detail",
  TRACKING_EVENT_VIEWED_WINE_DETAILS: "viewed_wine_details",
  TRACKING_EVENT_VIEWED_FAQ: "viewed_faq",
  TRACKING_EVENT_COMPLETED_TASTE_TEST_QUIZ: "completed_taste_test_quiz",
  TRACKING_EVENT_CLICKED_FEEDBACK_SURVEY: "clicked_feedback_survey",
  TRACKING_EVENT_CLICKED_WINE_101_COURSE_SIGNUP: "clicked_wine_101_course_signup",
  TRACKING_EVENT_DOWNLOADED_CORPORATE_ORDER_FORM: "downloaded_corporate_order_form",
  TRACKING_EVENT_SUBMITTED_CORPORATE_ORDER_FORM: "submitted_corporate_order_form",
  TRACKING_EVENT_FILTERED_TASTE_PROFILE_YOUR_PALATE_DATA: "filtered_taste_profile_your_palate_data",
  TRACKING_EVENT_VIEWED_TASTE_PROFILE_TAB: "viewed_taste_profile_tab",
  TRACKING_EVENT_CLICKED_TASTE_PROFILE_GUIDE_CHARACTERISTIC: "clicked_taste_profile_guide_characteristic",
  TRACKING_EVENT_CLICKED_UNLOCK_TASTE_PROFILE: "clicked_unlock_taste_profile",
  TRACKING_EVENT_RECEIVED_NO_RESULTS_FROM_RECOMMEND_SIMILAR_WINE:
    "received_no_results_from_recommend_similar_wine",
  TRACKING_EVENT_VIEWED_PREVIOUS_BOX_FROM_MY_NEXT_BOX: "viewed_previous_box_from_my_next_box",
  TRACKING_EVENT_CLICKED_BUILD_YOUR_OWN_BOX: "clicked_build_your_own_box",
  TRACKING_EVENT_CLICKED_PICK_YOUR_FREE_BOTTLE: "clicked_pick_your_free_bottle",
  TRACKING_EVENT_CLICKED_REWARD_YOURSELF: "clicked_reward_yourself",
  TRACKING_EVENT_CLICKED_FULL_ORDER_HISTORY: "clicked_full_order_history",
  TRACKING_EVENT_REFRESHED_RECOMMENDATION: "refreshed_recommendation",
  TRACKING_EVENT_CLICKED_GO_TO_SUBSCRIPTION_SETTINGS: "clicked_go_to_subscription_settings",
  TRACKING_EVENT_TOGGLED_TASTE_PROFILE_GRAPH_VIEW: "toggled_taste_profile_graph_view",
  TRACKING_EVENT_CONFIRMED_CONSENT_FOR_COOKIES: "confirmed_consent_for_cookies",
  TRACKING_EVENT_ADDED_WINE_RECOMMENDATION_CHALLENGES: "added_wine_recommendation_from_challenge_details",
  TRACKING_EVENT_ADDED_WINE_RECOMMENDATION_BADGES: "added_wine_recommendation_from_badge_details",
  TRACKING_EVENT_CLICKED_BEER_LANDING_HERO_CTA: "clicked_beer_landing_hero_cta",
  TRACKING_EVENT_CLICKED_BEER_LANDING_QUIZ_CTA: "clicked_beer_landing_quiz_cta",
  TRACKING_EVENT_CLICKED_ADVENT_CALENDAR_CTA: "clicked_advent_calendar_cta",
  TRACKING_EVENT_SELECTED_ADVENT_CALENDAR_OPTION: "selected_advent_calendar_option",
  TRACKING_EVENT_ADDED_ADVENT_CALENDAR_TO_CART: "added_advent_calendar_to_cart",
  TRACKING_EVENT_REVEALED_ADVENT_CALENDAR_WINES: "revealed_advent_calendar_wines",
  TRACKING_EVENT_STARTED_BUILD_YOUR_OWN_ADVENT: "started_build_your_own_advent",
  TRACKING_EVENT_REMOVED_BUILD_YOUR_OWN_ADVENT: "removed_build_your_own_advent",
  TRACKING_EVENT_STARTED_BEER_QUIZ: "started_beer_quiz",
  TRACKING_EVENT_VIEWED_BEER_QUIZ_STEP: "viewed_beer_quiz_step",
  TRACKING_EVENT_ANSWERED_BEER_QUIZ_QUESTION: "answered_beer_quiz_question",
  TRACKING_EVENT_COMPLETED_BEER_QUIZ: "completed_beer_quiz",
  TRACKING_EVENT_CLICKED_BEER_QUIZ_GET_THIS_BOX: "clicked_beer_quiz_get_this_box",
  TRACKING_EVENT_STARTED_BEER_CHECKOUT: "started_beer_checkout",
  TRACKING_EVENT_EXPANDED_WINE_EXPLANATION: "expanded_wine_explanation",
  TRACKING_EVENT_SUBMITTED_EXPLANATION_FEEDBACK: "submitted_wine_explanation_feedback",
  TRACKING_EVENT_SUBMITTED_MEAL_PAIRING: "submitted_meal_pairing",
  TRACKING_EVENT_ADDED_PAIRING_WINE_TO_CART: "added_pairing_wine_to_cart",
  TRACKING_EVENT_SUBMITTED_PAIRING_FEEDBACK: "submitted_pairing_feedback",
  // "Get a recommendation" funnel. Shared top (chooser open + mode fork) feeds
  // both the by-style path (events below) and the by-meal path (the
  // submitted_meal_pairing → added_pairing_wine_to_cart events above), so the
  // whole journey can be visualised as one funnel down to placed_order.
  TRACKING_EVENT_OPENED_RECOMMENDATION_CHOOSER: "opened_recommendation_chooser",
  TRACKING_EVENT_CHOSE_RECOMMENDATION_MODE: "chose_recommendation_mode",
  TRACKING_EVENT_REQUESTED_STYLE_RECOMMENDATION: "requested_style_recommendation",
  TRACKING_EVENT_RECEIVED_STYLE_RECOMMENDATION: "received_style_recommendation",
  TRACKING_EVENT_RECEIVED_NO_STYLE_RECOMMENDATION: "received_no_style_recommendation",
};

// Branch values for TRACKING_EVENT_CHOSE_RECOMMENDATION_MODE — the fork between
// the two "get a recommendation" paths.
const trackingEventRecommendationModes = {
  BY_STYLE: "by_style",
  BY_MEAL: "by_meal",
};

const trackingEventGiftOptions = {
  SHIP_SURPRISE_BOX: "ship_surprise_box",
  SEND_GIFT_VOUCHER: "send_gift_voucher",
  BUILD_OWN_GIFT_BOX: "build_own_gift_box",
};

const trackingEventDropCancellationOptions = {
  [CANCELLATION_CLOSE_TRAY]: "closed_cancellation_trays",
};

const trackingEventSavedListOptions = {
  [WINE_LIST_TYPE_IDS.DB_ID_WINE_LIST_TYPE_MEMBER_WISHLIST]: "viewed_my_wishlist",
  [WINE_LIST_TYPE_IDS.DB_ID_WINE_LIST_TYPE_MEMBER_FAVOURITES]: "viewed_my_favourites",
  [WINE_LIST_TYPE_IDS.DB_ID_WINE_LIST_TYPE_MEMBER_CUSTOM]: "viewed_custom_list",
};

const trackingEventBoxAndSpendOptions = {
  IS_SPECIFIC_BOX_AND_SPEND: "is_specific_box_and_spend",
  MAXIMUM_SPEND_PER_MONTH: "max_spend_per_month",
  AVERAGE_BOTTLE_SPEND: "average_bottle_spend",
  NUMBER_OF_BOTTLES: "number_of_bottles",
};

const trackingEventGrapesToAvoidOptions = {
  IS_OPTION_TURNED_ON: "is_option_turned_on",
  GRAPES_TO_AVOID_TYPE_ID: "grapes_to_avoid_type_id",
  GRAPE_TYPE_ID: "grape_type_id",
  OPTION_LABEL: "option_label",
};

const trackingEventDeliveryFrequencyOptions = {
  PREVIOUS_DELIVERY_FREQUENCY: "previous_delivery_frequency",
  NEW_DELIVERY_FREQUENCY: "new_delivery_frequency",
};

const trackingEventClickedTagOnProductCardOptions = {
  TAG_NAME: "tag_name",
  TAG_VALUE: "tag_value",
  IS_FROM_DETAILS: "is_from_details",
};

// Values for the `rating_method` property on selected_product_rating events
const trackingEventSelectedProductRatingMethods = {
  TAP: "tap",
  SLIDE: "slide",
  CLICK: "click",
  KEYBOARD: "keyboard",
};

const trackingEventSearchedWineOptions = {
  SEARCH_PHRASE: "search_phrase",
};

const trackingEventSortedWineOptions = {
  ORDER_BY: "order_by",
};

const trackingEventViewedWineDetailsOptions = {
  WINE_ID: "wine_id",
  WINE_SLUG: "wine_slug",
};

const trackingEventTasteTestQuizOptions = {
  WINE_SLUG: "wine_slug",
  SCORE: "score",
  AVERAGE_SCORE: "average_score",
};

const trackingEventClickedFeedbackSurveyOptions = {
  SURVEY_NAME: "survey_name",
};

const trackingEventClickedTasteProfileGuideCharacteristicOptions = {
  WINE_CHARACTERISTIC_NAME: "wine_characteristic_name",
  WINE_CHARACTERISTIC_ID: "wine_characteristic_id",
};

const trackingEventFilteredTasteProfileYourPalateDataOptions = {
  SELECTED_FILTER: "selected_filter",
  SELECTED_WINE_TYPE: "selected_wine_type",
};

const trackingEventClickedUnlockTasteProfileOptions = {
  RATINGS_NEEDED: "ratings_needed",
};

const trackingEventReceivedNoResultsFromRecommendSimilarWineOptions = {
  WINE_CLASS_ID: "wine_class_id",
  WINE_PRICE_RANGE_ID: "wine_price_range_id",
  EXCLUDE_WINE_IDS: "exclude_wine_ids",
};

const trackingEventRetookQuizOptions = {
  QUIZ_VERSION_ID: "quiz_version_id",
  QUIZ_ORIGIN: "quiz_origin",
};

const trackingEventQuizOrigins = {
  TASTE_PROFILE: "taste_profile",
  SETTINGS: "settings",
};

const trackingEventPalateEditOptions = {
  ORIGIN: "origin",
  TRAIT: "trait",
  WINE_CLASS_GROUP: "wine_class_group",
  LEVEL: "level",
  PREVIOUS_LEVEL: "previous_level",
  CHANGED_GROUP_COUNT: "changed_group_count",
};

const trackingEventPalateEditOrigins = {
  TASTE_PROFILE_PALATE: "taste_profile_palate",
};

/**
 * String labels used as the `quiz_version` parameter on quiz tracking events,
 * keyed by DB_IDS_QUIZ_VERSION. BigQuery / Metabase funnels filter on these.
 * @type {Record<number, string>}
 */
const QUIZ_VERSION_NAMES = {
  1: "basic", // DB_IDS_QUIZ_VERSION.SIMPLE_QUIZ
  2: "advanced", // DB_IDS_QUIZ_VERSION.ADVANCED_QUIZ
};

const trackingEventQuizStepOptions = {
  QUIZ_VERSION: "quiz_version",
  QUIZ_VERSION_ID: "quiz_version_id",
  STEP_NUMBER: "step_number",
  STEP_NAME: "step_name",
  STEP_KIND: "step_kind",
  QUIZ_QUESTION_ID: "quiz_question_id",
  IS_SUBSCRIBER: "is_subscriber",
  IS_LOGGED_IN: "is_logged_in",
  // True when the user has taken the quiz before — i.e. logged in with prior
  // quiz answers, or arrived via the /quiz/<version>/ retake route. Lets the
  // funnel for "new users" filter on `is_retake = false`.
  IS_RETAKE: "is_retake",
};

const trackingEventSwitchedQuizVersionOptions = {
  FROM_QUIZ_VERSION: "from_quiz_version",
  TO_QUIZ_VERSION: "to_quiz_version",
  TRIGGERED_AT_STEP_NAME: "triggered_at_step_name",
  TRIGGERED_AT_STEP_NUMBER: "triggered_at_step_number",
};

const trackingEventRefreshedRecommendationOptions = {
  REQUESTED_WINE_CLASS_ID: "requested_wine_class_id",
  REQUESTED_WINE_PRICE_RANGE_ID: "requested_wine_price_range_id",
  EXCLUDE_WINE_IDS: "exclude_wine_ids",
  IS_SUCCESSFUL: "is_successful",
  IS_NEW_COLOUR_AND_PRICE: "is_new_colour_and_price",
};

const trackingEventWineRecommendationOptions = {
  WINE_COLOUR: "wine_colour",
  WINE_COLOUR_ID: "wine_colour_id",
  WINE_GRAPE_TYPE: "wine_grape_type",
  WINE_GRAPE_TYPE_ID: "wine_grape_type_id",
  WINE_NAME: "wine_name",
  WINE_PRICE: "wine_price",
};

const errorMessagesToIgnoreInIterable = [
  ERROR_MESSAGE_ITERABLE_USER_UNAUTHORIZED,
  `AxiosError: ${ERROR_MESSAGE_ITERABLE_USER_UNAUTHORIZED}`,
  ERROR_MESSAGE_ITERABLE_CONFLICT,
  `AxiosError: ${ERROR_MESSAGE_ITERABLE_CONFLICT}`,
  ERROR_MESSAGE_ITERABLE_BAD_REQUEST,
  `AxiosError: ${ERROR_MESSAGE_ITERABLE_BAD_REQUEST}`,
  ERROR_MESSAGE_ITERABLE_NETWORK,
  `AxiosError: ${ERROR_MESSAGE_ITERABLE_NETWORK}`,
];

const errorCodesToIgnoreInIterable = [ERROR_CODE_ITERABLE_FORGOTTEN_USER, ERROR_CODE_ITERABLE_GDPR_FORGOTTEN];

/**
 * Formats the data for the filtered wine event.
 * @param {Object} selectedFilters
 * @return {Object}
 */
const formatTrackingEventDataFilteredWines = (selectedFilters) => ({
  country_ids: selectedFilters.countryIds || null,
  search_phrase: selectedFilters.name || null,
  order_by: selectedFilters.orderBy || null,
  wine_acidity_id: selectedFilters.wineAcidityId || null,
  wine_body_id: selectedFilters.wineBodyId || null,
  wine_class_ids: selectedFilters.wineClassIds || null,
  wine_fruit_driven_id: selectedFilters.wineFruitDrivenId || null,
  wine_list_slug: selectedFilters.wineListSlug || null,
  wine_price_range_ids: selectedFilters.winePriceRangeIds || null,
  wine_production_methods_ids: selectedFilters.wineProductionMethodIds || null,
  wine_region_ids: selectedFilters.wineRegionIds || null,
  wine_sweetness_id: selectedFilters.wineSweetnessId || null,
  wine_tannin_id: selectedFilters.wineTanninId || null,
  wine_type_id: selectedFilters.wineTypeIds || null,
});

/**
 * Maps custom event names to standard Facebook event names.
 *
 * @param {string} event
 * @return {string}
 * */
const mapCustomEventToStandardFbEvent = (event) => {
  switch (event) {
    case trackingEvents.TRACKING_EVENT_PLACED_ORDER:
      return TrackableEventNameEnum.Purchase;
    case trackingEvents.TRACKING_EVENT_COMPLETED_QUIZ:
      return TrackableEventNameEnum.Lead;
    case trackingEvents.TRACKING_EVENT_STARTED_CHECKOUT:
      return TrackableEventNameEnum.InitiateCheckout;
    default:
      return event;
  }
};

/**
 * Maps custom event names to standard Google event names.
 * Recommended event names:
 * https://developers.google.com/analytics/devguides/collection/ga4/reference/events?client_type=gtag
 * @param {string} event
 * @returns {string}
 */
const mapEventToGoogleTagEvent = (event) => {
  switch (event) {
    case trackingEvents.TRACKING_EVENT_STARTED_CHECKOUT:
      return "begin_checkout";
    case trackingEvents.TRACKING_EVENT_VISITED_PAGE:
      return "page_view";
    case trackingEvents.TRACKING_EVENT_COMPLETED_QUIZ:
      return "generate_lead";
    case trackingEvents.TRACKING_EVENT_SEARCHED_WINES:
      return "search";
    default:
      return event;
  }
};

// Option when website is opened in mobile app.
const TRACKING_EVENT_OPTION_IS_MOBILE_APP = "is_mobile";

const trackIterableEvent = async (event, data) => {
  try {
    const { track } = await loadIterable();
    const response = await track({
      eventName: event,
      dataFields: data,
    });
    const responseData = response?.data ?? {};
    // Skip logging for GDPR email forgotten email.

    if (responseData?.code === RESPONSE_CODE_ITERABLE_SUCCESS) {
      return;
    }

    if (!errorCodesToIgnoreInIterable.includes(responseData?.code)) {
      const errorMessage = `${
        responseData?.code
      } - Error tracking: '${event}' event in Iterable. ${JSON.stringify(response)}`;
      logError(errorMessage, response);
    }
  } catch (error) {
    if (!errorMessagesToIgnoreInIterable.includes(error?.message)) {
      const errorMessage = `Error tracking: '${event}' event in Iterable. ${JSON.stringify(error)}`;
      logError(errorMessage, error);
    }
  }
};

/**
 * Tracks an ecommerce event using a 3rd party library.
 * Currently using Iterable
 * @param {string} event
 * @param {Object} data
 */
const trackEcommerceEvent = (event, data, isIterableReady = true) => {
  const ecommerceExtraSettings = {
    integrations: {
      All: false,
      // Destination flags are case sensitive and match the destination’s name in the docs
      "Google Analytics 4 Web": true, // Web version
      "Google Analytics 4": true, // Cloud version
      "Google Analytics": true, // Legacy version
      "Google Tag Manager": true,
      "Google AdWords New": true,
      Iterable: true,
    },
  };
  const ecommerceData = {
    order_id: data.order_id,
    currency: FACEBOOK_TRACKING_CURRENCY,
    revenue: data.revenue,
    value: data.value,
    total: data.revenue,
    discount: data.order_discount,
    coupon: data.order_discount_code,
    category: "Order",
    label: data.order_category_name,
    order_category_id: data.order_category_id,
    order_category_name: data.order_category_name,
    products: [],
    email: data.email,
    ...getUtmParameters(),
  };
  const items = []; // FOr Google Tag Manager.
  if (data?.order_items?.length) {
    data.order_items.forEach((item) => {
      ecommerceData.products.push({
        // Iterable expects product_id to be of type string
        product_id: item.product_id.toString(),
        sku: item.product_sku,
        name: item.product_name,
        quantity: item.quantity,
        price: item.product_price,
      });

      // Google Tag Manager
      items.push({
        item_name: item.product_name,
        item_id: item.product_sku,
        quantity: item.quantity,
        price: item.product_price,
      });
    });
    const ecommerceEvent = "Order Completed";

    // Data for Google Tag Manager.
    const gtagEvent = "purchase";
    const gtagEventData = { ...ecommerceData };
    gtagEventData.items = items;
    window.gtag?.("event", gtagEvent, gtagEventData);

    const isMemberLoggedIn = isLoggedIn();
    if (hasIterableApiKey() && isMemberLoggedIn && isIterableReady) {
      const dataFields = {
        ...ecommerceData,
        ...ecommerceExtraSettings,
      };
      trackIterableEvent(ecommerceEvent, dataFields);
    }
  }
  return true;
};

/**
 * Tracks an event using a 3rd party library.
 * Currently using Iterable.
 * @param {string} event
 * @param {Object} data
 * @param {Boolean} isDuplicate
 */

export const useTrackEvent = () => {
  const { isIterableReady, facebookPixel } = useAppContext();

  const trackEvent = (event, data, isDuplicate = false, forceIsIterableReady = false) => {
    if (event === trackingEvents.TRACKING_EVENT_PLACED_ORDER) {
      if (!data.value) {
        return false;
      }
      // Sends first any analytics tracking events for ECOMMERCE
      trackEcommerceEvent(event, data, isIterableReady);
    }

    // isDuplicate means we already tracked this event, but need a second call to send
    // data for third-party who requires a specific setting. (e.g. Facebook does not accept
    // the backend order placed event, so we re-send it on the front end here).
    const extraSettings = isDuplicate && {
      integrations: {
        All: false,
        "Facebook Pixel": true,
        "Pinterest Tag": true,
        "Google Tag Manager": true,
        "Google AdWords New": true,
      },
    };

    const modifiedData = {
      ...data,
      website_variant: WEBSITE_VARIANT,
      event_url: window.location.href,
      ...getUtmParameters(),
    };

    // Track event to Pixel
    // Strip PII fields (email, firstName) to comply with Meta Pixel Business Tools Terms.
    // Destructure so we don't mutate modifiedData, which is reused by Iterable and gtag below.
    const fbEvent = mapCustomEventToStandardFbEvent(event);
    const { email: _email, firstName: _firstName, ...dataForFbPixel } = modifiedData;
    facebookPixel?.trackEvent(fbEvent, dataForFbPixel);

    // Track event to Iterable
    const isMemberLoggedIn = isLoggedIn();
    const isIterableReadyToTrackEvent = forceIsIterableReady || isIterableReady;
    // Iterable configuration must not suppress Google or Facebook events.
    if (hasIterableApiKey() && isMemberLoggedIn && isIterableReadyToTrackEvent) {
      const dataFields = {
        ...modifiedData,
        ...extraSettings,
      };
      trackIterableEvent(event, dataFields);
    }

    const gtagEvent = mapEventToGoogleTagEvent(event);
    window.gtag?.("event", gtagEvent, modifiedData);

    return true;
  };

  return trackEvent;
};

const identifyIterableUser = async (analyticsIdentityData) => {
  if (!hasIterableApiKey()) return false;

  try {
    const { updateUser } = await loadIterable();
    await updateUser(analyticsIdentityData, true, true);
  } catch (error) {
    if (!errorMessagesToIgnoreInIterable.includes(error?.message)) {
      const errorMessage = `Error updating user in Iterable. ${JSON.stringify(error)}`;
      logError(errorMessage, error);
    }
  }
  return true;
};

const trackIterablePageVisited = (title, pageVisitedExtraData) => {
  const dataFields = {
    title,
    website_variant: WEBSITE_VARIANT,
    ...pageVisitedExtraData,
  };

  loadIterable()
    .then(({ track }) =>
      track({
        eventName: trackingEvents.TRACKING_EVENT_VISITED_PAGE,
        dataFields,
      })
    )
    .catch((error) => {
      if (!errorMessagesToIgnoreInIterable.includes(error?.message)) {
        const errorMessage = `Error tracking Page Visited event in Iterable. ${JSON.stringify(error)}`;
        logError(errorMessage, error);
      }
    });
};

export const useTrackPageVisited = () => {
  const { facebookPixel, isIterableReady } = useAppContext();

  const trackPageVisited = (title, pageVisitedExtraData = {}) => {
    if (hasIterableApiKey() && isIterableReady) {
      trackIterablePageVisited(title, pageVisitedExtraData);
    }

    facebookPixel?.trackEvent(TrackableEventNameEnum.PageView, {
      title,
      website_variant: WEBSITE_VARIANT,
      ...pageVisitedExtraData,
    });

    const gtagEvent = mapEventToGoogleTagEvent(trackingEvents.TRACKING_EVENT_VISITED_PAGE);
    window.gtag("event", gtagEvent, pageVisitedExtraData);

    return hasIterableApiKey();
  };

  return trackPageVisited;
};

/**
 * Returns the UTM parameters used to track the performance of a marketing campaign.
 * TODO: We should be using the UTM parameters from the URL and storing them in cookies,
 * but as a temporary solution we are using the Iterable cookies directly.
 * @returns {Object}
 */
const getUtmParameters = () => {
  if (Cookies.get("iterableEmailCampaignId")) {
    return {
      utm_campaign: Cookies.get("iterableEmailCampaignId"),
      utm_source: "iterable",
      utm_medium: "email",
      utm_content: Cookies.get("iterableTemplateId") ?? "",
    };
  }
  return {};
};

export {
  identifyIterableUser,
  trackingEvents,
  trackingEventRecommendationModes,
  trackingEventGiftOptions,
  trackingEventDropCancellationOptions,
  trackingEventSavedListOptions,
  trackingEventBoxAndSpendOptions,
  trackingEventGrapesToAvoidOptions,
  trackingEventDeliveryFrequencyOptions,
  trackingEventClickedTagOnProductCardOptions,
  formatTrackingEventDataFilteredWines,
  trackingEventSelectedProductRatingMethods,
  trackingEventSearchedWineOptions,
  trackingEventSortedWineOptions,
  trackingEventViewedWineDetailsOptions,
  trackingEventTasteTestQuizOptions,
  trackingEventClickedFeedbackSurveyOptions,
  trackingEventClickedTasteProfileGuideCharacteristicOptions,
  trackingEventFilteredTasteProfileYourPalateDataOptions,
  trackingEventClickedUnlockTasteProfileOptions,
  trackingEventReceivedNoResultsFromRecommendSimilarWineOptions,
  trackingEventRefreshedRecommendationOptions,
  trackingEventRetookQuizOptions,
  trackingEventQuizOrigins,
  trackingEventPalateEditOptions,
  trackingEventPalateEditOrigins,
  trackingEventQuizStepOptions,
  trackingEventSwitchedQuizVersionOptions,
  QUIZ_VERSION_NAMES,
  TRACKING_EVENT_OPTION_IS_MOBILE_APP,
  trackingEventWineRecommendationOptions,
  getUtmParameters,
};
